import { useMemo, useState } from "react";

import {
  faCheck,
  faChevronDown,
  faExclamationCircle,
  faFile,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { useFilesQuery } from "src/api/filesApi/queries";
import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import {
  CaseEnvironment,
  FilesViewerCaseBlockConfigAttributes,
} from "src/clients/case-management";
import { GetFilesEnvFilesGetEnvEnum } from "src/clients/files";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { Skeleton } from "src/design-system/Skeleton";
import { FileViewerContent, getFileGroup } from "src/files/FileViewer";

type FilesViewerBlockProps = Omit<CaseBlockProps, "children"> & {
  data: FilesViewerCaseBlockConfigAttributes;
  caseEnv: CaseEnvironment;
};

type FilePickerValue = {
  fileId: string;
  /** Undefined when the file could not be resolved. */
  fileName: string | undefined;
};

const ID_PREVIEW_LENGTH = 5;

const unavailableTriggerLabel = (fileId: string) => {
  const preview =
    fileId.length > ID_PREVIEW_LENGTH
      ? `${fileId.slice(0, ID_PREVIEW_LENGTH)}...`
      : fileId;

  return `File unavailable (${preview})`;
};

const UnavailableFileLabel: React.FC<{ fileId: string }> = ({ fileId }) => (
  <>
    <Icon icon={faTriangleExclamation} size="sm" variant="error" />
    <span className="shrink-0 whitespace-nowrap">File unavailable</span>
    <span className="flex min-w-0 flex-1">
      <TruncatedIdPill
        dataLoc="files-viewer-file-picker-id-pill"
        id={fileId}
        variant="middle-truncated"
      />
    </span>
  </>
);

export const FilesViewerBlock: React.FC<FilesViewerBlockProps> = ({
  data,
  caseEnv,
  ...props
}) => {
  const fileIds = useMemo(() => data.files.map(({ id }) => id), [data.files]);
  const locatorsById = useMemo(
    () => new Map(data.files.map(({ id, locator }) => [id, locator])),
    [data.files],
  );

  const {
    data: filesResponse,
    isLoading,
    isError,
  } = useFilesQuery(fileIds, caseEnv as GetFilesEnvFilesGetEnvEnum);

  const [pickedFileId, setPickedFileId] = useState<string | null>(null);
  const selectedFileId =
    pickedFileId && fileIds.includes(pickedFileId) ? pickedFileId : fileIds[0];

  const filesById = useMemo(() => {
    const files = filesResponse?.data.data ?? [];

    return new Map(files.map((file) => [file.id, file.attributes]));
  }, [filesResponse]);

  // Keep the configured order, so a file that failed to resolve stays in place
  // instead of dropping out of the picker.
  const elements = useMemo(
    () =>
      fileIds.map((fileId) => ({
        key: fileId,
        value: { fileId, fileName: filesById.get(fileId)?.name },
      })),
    [fileIds, filesById],
  );

  if (!fileIds.length) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="files-viewer-empty-state"
          description="No files have been added to this block yet."
          headline="No files"
          icon={faFile}
          size="compact"
        />
      </CaseBlock>
    );
  }

  if (isLoading) {
    const filePickerPlaceholder = fileIds.length > 1 && (
      <Skeleton
        dataLoc="files-viewer-file-picker-loading"
        height="h-5"
        width="w-24"
      />
    );

    return (
      <CaseBlock
        {...props}
        expandedRightSlot={filePickerPlaceholder}
        isExpandable
        noPaddings
        noScroll
        rightSlot={filePickerPlaceholder}
      >
        <div className="flex flex-1 flex-col p-4">
          <Skeleton
            dataLoc="files-viewer-content-loading"
            height="h-full"
            width="w-full"
          />
        </div>
      </CaseBlock>
    );
  }

  if (isError) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="files-viewer-error-state"
          description="The files could not be loaded. They might be missing or you might not have access to them."
          headline="Files unavailable"
          icon={faExclamationCircle}
          size="compact"
          variant="error"
        />
      </CaseBlock>
    );
  }

  const selectedFile = filesById.get(selectedFileId);

  const filePicker = fileIds.length > 1 && (
    <FixedPositionedDropdown<FilePickerValue, string>
      buttonClassName="h-fit w-fit rounded border-transparent bg-transparent p-0"
      buttonDataLoc="files-viewer-file-picker-trigger"
      dataLoc="files-viewer-file-picker"
      elements={elements}
      listMatchesButtonWidth={false}
      placement="bottom end"
      renderButtonValue={(file) => (
        <Pill
          dataLoc="files-viewer-file-picker-pill"
          size="sm"
          variant="gray"
          maxWidth
        >
          {file && file.fileName === undefined && (
            <Pill.Icon icon={faTriangleExclamation} />
          )}
          <Pill.Text>
            {file
              ? (file.fileName ?? unavailableTriggerLabel(file.fileId))
              : ""}
          </Pill.Text>
          <Pill.Icon icon={faChevronDown} />
        </Pill>
      )}
      renderValue={({ key, value, selected }) => (
        <span
          className="flex min-w-0 flex-1 items-center gap-x-2"
          data-loc={`files-viewer-file-picker-option-${key}`}
        >
          {value.fileName === undefined ? (
            <UnavailableFileLabel fileId={value.fileId} />
          ) : (
            <span className="truncate">{value.fileName}</span>
          )}
          {selected && (
            <span className="ml-auto">
              <Icon icon={faCheck} size="sm" variant="active" />
            </span>
          )}
        </span>
      )}
      selected={selectedFileId}
      onSelect={setPickedFileId}
    />
  );

  const contentGroup = selectedFile
    ? getFileGroup(selectedFile.content_type)
    : "unsupported";

  // FileViewerContent has inconsistent paddings
  // depending on the content group.
  // We handle it per-group here.
  // TOOD: Remove when fixed in FileViewerContent.
  const wrapperClassName = twJoin(
    "flex h-full w-full flex-1 flex-col",
    (contentGroup === "xlsx" || contentGroup === "csv") && "p-4 pt-2",
    (contentGroup === "json" || contentGroup === "text") && "p-4",
  );

  return (
    <CaseBlock
      {...props}
      expandedRightSlot={filePicker}
      isExpandable
      noPaddings
      noScroll
      rightSlot={filePicker}
    >
      <div className={wrapperClassName}>
        {selectedFile ? (
          <FileViewerContent
            file={selectedFile}
            locator={locatorsById.get(selectedFileId)}
          />
        ) : (
          <EmptyState
            dataLoc="files-viewer-file-error-state"
            description="The file could not be loaded. It might be missing or you might not have access to it."
            headline="File unavailable"
            icon={faExclamationCircle}
            size="compact"
            variant="error"
          />
        )}
      </div>
    </CaseBlock>
  );
};
